import { Stack } from '@/core/blocks';
import { HtmlMarkup, Skeleton, Typography } from '@/core/ui';
import { cn } from '@/lib';
import { useAppSelector } from '@/store';
import { API } from '@/store/api';
import { useTranslation } from 'react-i18next';

export const CupDetail = () => {
  const { i18n } = useTranslation();
  const competitionId = useAppSelector((s) => s.cup.competitionId);

  const { data, error, isFetching, isLoading } = API.cupApi.useGetCompetitionDetailQuery(
    {
      locale: i18n.language,
      competitionId: competitionId as number,
    },
    { skip: !competitionId || !i18n.language },
  );

  if (error) {
    return <p>Oh no, there was an error</p>;
  }

  if (isFetching || isLoading || !data) {
    return (
      <div className='bg-linear-to-r/srgb from-olive/90 via-apple to-apple rounded-2xl h-full w-full'>
        <div className={cn('flex flex-row items-center h-full w-full')}>
          <div className='flex flex-col w-full items-center justify-between space-y-10 basis-5/12 p-[105px]'>
            <Skeleton className='rounded-md w-[400px] h-[450px]' />
          </div>
          <div className='w-full basis-7/12 pl-0 p-[105px]'>
            <div className='flex flex-col space-y-8'>
              <Skeleton className='h-[50px] w-96' />
              <Skeleton className='h-[275px] w-[664px]' />
            </div>
            <div className='flex flex-col mt-10 space-y-[38px]'>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className='flex justify-between items-center'>
                  <div className='flex space-x-6'>
                    <Stack upperClass='w-[10px] h-[20px]' lowerClass='w-[10px] h-[16px]' />
                    <Skeleton className='h-[50px] w-72' />
                  </div>
                  <Skeleton className='h-[50px] w-20' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-apple rounded-2xl h-full w-full'>
      <div className={cn('flex flex-row items-center h-full w-full bg-player bg-no-repeat')}>
        <div className='flex flex-col w-full items-center justify-between space-y-10 basis-5/12 p-[105px]'>
          {data?.logo.path ? (
            <img
              alt={data?.logo.caption || ''}
              src={data?.logo.path}
              className='w-[400px] h-[450px] object-cover'
            />
          ) : (
            <img alt='player' src='/default.png' className='bg-chalk w-[361px] h-[361px] object-cover' />
          )}
        </div>
        <div className='w-full basis-7/12 space-y-12 pl-0 p-[105px]'>
          <div className='flex flex-col space-y-8'>
            <div className='max-w-2xl'>
              <Typography className='text-[45px] block max-w-md font-semibold leading-[42.75px] tracking-[3.15px] uppercase'>
                {data?.name}
              </Typography>
            </div>
            <div className='flex flex-col max-w-[664px] space-y-7'>
              <HtmlMarkup className='text-2xl leading-[27.6px] tracking-[1.2px]'>
                {data?.description}
              </HtmlMarkup>
            </div>
          </div>
          <div className='flex flex-col space-y-[38px]'>
            {data?.extra_info && Array.isArray(data.extra_info) && data.extra_info.length > 0 ? (
              data.extra_info.map((i) => (
                <div key={i.title} className='flex justify-between items-center'>
                  <div className='flex space-x-6'>
                    <Stack upperClass='w-[10px] h-[23px]' lowerClass='w-[10px] h-[13px]' />
                    <Typography className='text-[45px] font-semibold leading-[45px] tracking-[3.15px] uppercase'>
                      {i.title}
                    </Typography>
                  </div>
                  <Typography className='text-[50px] font-bold leading-[50px] tracking-[3.5px] uppercase'>
                    {i.value}
                  </Typography>
                </div>
              ))
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
